import React, { useState } from 'react';
import { TemplateStatus } from '~/types';

interface TemplateVariable {
  name: string;
  position: number;
  example: string;
  component_type: string;
}

interface QuickReplyTemplate {
  _id: string;
  template_id: string;
  meta_template_name: string;
  status: TemplateStatus;
  template: {
    tbit_name: string;
    language: string;
    components: any[];
    variables?: TemplateVariable[];
  };
}

interface AvailableTemplatesProps {
  templates: QuickReplyTemplate[];
  selectedTemplate: QuickReplyTemplate | null;
  setSelectedTemplate: (template: QuickReplyTemplate | null) => void;
  loading: boolean;
  onRefresh: () => void;
  onCreateNew: () => void;
  onDelete: (templateId: string) => void;
  t: (key: string) => string;
}

const AvailableTemplates: React.FC<AvailableTemplatesProps> = ({
  templates,
  selectedTemplate,
  setSelectedTemplate,
  loading,
  onRefresh,
  onCreateNew,
  onDelete,
  t,
}) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(
    null,
  );
  const [deletingTemplate, setDeletingTemplate] = useState(false);

  const handleDeleteClick = (templateId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmDelete(templateId);
  };

  const handleDeleteConfirm = async (
    templateId: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setDeletingTemplate(true);
    await onDelete(templateId);
    setDeletingTemplate(false);
    setShowConfirmDelete(null);
  };

  const handleDeleteCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmDelete(null);
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRefresh();
  };

  const handleCreateNew = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCreateNew();
  };

  const getStatusBadgeColor = (status: TemplateStatus) => {
    switch (status) {
      case 'APPROVED':
        return 'badge-success text-white';
      case 'PENDING':
        return 'badge-warning text-gray-900';
      case 'REJECTED':
        return 'badge-error text-white';
      default:
        return 'badge-info text-gray-900';
    }
  };

  // Render a template preview with variable placeholders highlighted and named
  const renderTemplatePreview = (template: QuickReplyTemplate) => {
    if (!template.template.components?.[0]?.text) return null;

    let previewText = template.template.components[0].text;

    // If this template has variables, highlight them in the preview
    if (template.template.variables?.length) {
      // Sort by position in descending order to avoid offset issues when replacing
      const sortedVars = [...template.template.variables].sort(
        (a, b) => b.position - a.position,
      );

      for (const variable of sortedVars) {
        const value =
          selectedTemplate === template
            ? `{{${variable.name}}}`
            : variable.example || `{{${variable.name}}}`;

        // Replace the {{number}} placeholder with the variable name or example
        previewText = previewText.replace(
          new RegExp(`\\{\\{${variable.position}\\}\\}`, 'g'),
          `<span class="text-primary font-medium bg-primary bg-opacity-10 px-1 rounded">${value}</span>`,
        );
      }
    }

    return (
      <div
        className="mt-1 text-sm text-gray-600"
        dangerouslySetInnerHTML={{ __html: previewText }}
      />
    );
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{t('available_templates')}</h3>
        <div className="flex space-x-2">
          <button onClick={handleRefresh} disabled={loading}>
            {loading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
          </button>
          <button
            className="btn btn-primary rounded-full"
            onClick={handleCreateNew}
          >
            {t('create_new')}
          </button>
        </div>
      </div>

      <div className="max-h-[300px] space-y-3 overflow-y-auto">
        {templates.map((template) => (
          <div
            key={template._id}
            className={`cursor-pointer rounded-lg border p-3 transition-all ${
              selectedTemplate?._id === template._id
                ? 'border-primary bg-primary bg-opacity-5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => {
              if (selectedTemplate?._id === template._id) {
                setSelectedTemplate(null);
              } else {
                setSelectedTemplate(template);
              }
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium">
                    {template.template.tbit_name || template.meta_template_name}
                  </h4>
                  <span
                    className={`badge ${getStatusBadgeColor(template.status)}`}
                  >
                    {t(template.status.toLowerCase())}
                  </span>
                </div>
                {renderTemplatePreview(template)}
                {template.template.variables &&
                  template.template.variables.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {template.template.variables.map((variable) => (
                        <span
                          key={variable.name}
                          className="badge badge-outline badge-sm text-gray-900"
                        >
                          {variable.name}
                        </span>
                      ))}
                    </div>
                  )}
              </div>

              <div className="flex space-x-2">
                {showConfirmDelete === template.template_id ? (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600">
                      {t('confirm_delete')}
                    </span>
                    <button
                      className="btn btn-error btn-xs"
                      onClick={(e) =>
                        handleDeleteConfirm(template.template_id, e)
                      }
                      disabled={deletingTemplate}
                    >
                      {deletingTemplate ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        t('yes')
                      )}
                    </button>
                    <button className="btn btn-xs" onClick={handleDeleteCancel}>
                      {t('no')}
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={(e) => handleDeleteClick(template.template_id, e)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvailableTemplates;
